/**
 * Five-number summary helpers used by the IQ ranking box plots. All
 * functions are pure and never mutate their inputs.
 */

export interface FiveNumber {
    min: number;
    p25: number;
    median: number;
    p75: number;
    max: number;
    /** Used as a clamp boundary when one huge outlier would squish the IQR box. */
    p95: number;
}

/**
 * Compute the value at percentile `p` (0..100) over a SORTED-ASCENDING
 * array, using nearest-index (no interpolation). Returns 0 if `sorted`
 * is empty.
 */
export const percentile = (
    sorted: ReadonlyArray<number>,
    p: number
): number => {
    if (sorted.length === 0) return 0;
    const idx = Math.min(
        sorted.length - 1,
        Math.floor((p / 100) * (sorted.length - 1))
    );
    return sorted[idx];
};

/**
 * Compute the five-number summary (plus P95) for an array of values.
 * Returns `undefined` when the input is empty.
 *
 * The input is not mutated — we sort an internal copy.
 */
export const fiveNumberSummary = (
    xs: ReadonlyArray<number>
): FiveNumber | undefined => {
    if (xs.length === 0) return undefined;
    const s = [...xs].sort((a, b) => a - b);
    return {
        min: s[0],
        p25: percentile(s, 25),
        median: percentile(s, 50),
        p75: percentile(s, 75),
        max: s[s.length - 1],
        p95: percentile(s, 95)
    };
};
